/**
 * The letters that ship with the module.
 *
 * In code rather than seeded into the database, so they cannot be half-present
 * on one environment, and so a new one arrives with a deploy instead of with a
 * migration somebody has to remember to run. They are STARTING POINTS: applying
 * one copies its blocks into the campaign, and nothing links back afterwards.
 *
 * This file only assembles them. The shape of a preset lives in `shared.ts` and
 * the copy lives one file per family, because copy is what keeps growing.
 *
 * Every preset ends with a footer block, which is the block that carries the
 * opt-out token. That is not a style choice - a commercial message owes its
 * reader a free and easy way to refuse, and a preset that starts somebody off
 * without one is a preset that ships that omission.
 */
import { renderEmailHtml } from "../render";
import { renderEmailText } from "../text";
import { cloneBlock, DESIGN_VERSION, type EmailDesign } from "../types";
import { HONEST_LOOK_PRESETS } from "./honestLook";
import { pick, type Preset, type PresetLocale } from "./shared";
import { STARTER_PRESETS } from "./starters";

export type { Preset, PresetLocale } from "./shared";

// Shapes first, then the named campaign. The order is what the library page
// shows, and an operator who does not know the project should still meet a
// plain letter before a branded one.
export const PRESETS: Preset[] = [...STARTER_PRESETS, ...HONEST_LOOK_PRESETS];

export function findPreset(id: string): Preset | null {
  return PRESETS.find((p) => p.id === id) ?? null;
}

/**
 * A preset ready to be put into a letter.
 *
 * Built fresh and then cloned, so two campaigns from the same preset do not
 * share block ids, and so editing one letter cannot reach into the module's own
 * constants - they are plain objects and would otherwise be mutated in place.
 */
export function instantiate(
  preset: Preset,
  locale: PresetLocale,
): { design: EmailDesign; bodyHtml: string; bodyText: string; subject: string } {
  const built = preset.build(locale);
  const copy: EmailDesign = {
    version: DESIGN_VERSION,
    page: { ...built.page },
    blocks: built.blocks.map(cloneBlock),
  };
  return {
    design: copy,
    bodyHtml: renderEmailHtml(copy),
    bodyText: renderEmailText(copy),
    subject: pick(preset.subject, locale),
  };
}
